import React, { useState } from 'react';
import { connect } from 'react-redux';

import * as favoritesModalActions from 'containers/PageHeader/FavoritesModal/actions';
import * as createGroupModalActions from 'containers/PageHeader/Groups/store/actions';
import * as exportModalActions from 'containers/PageHeader/ExportModal/actions';
import * as pageHeaderActions from 'containers/PageHeader/actions';
import * as globalSelectors from 'store/global/selectors';
import {
  basicOriginalPagesFiltersSelector,
  globalFilterWeeksSelector,
  pagesFiltersSelector,
} from 'containers/PageHeader/selectors';

import PageHeader from 'components/PageHeader';
import FavoritesModal from 'containers/PageHeader/FavoritesModal';
import CreateGroupModal from 'containers/PageHeader/Groups/CreateEdit';
import ExportModal from 'containers/PageHeader/ExportModal';
import FilterModal from 'containers/PageHeader/FilterModal';

import {
  FilterSelectionType,
  FilterType,
  GlobalFilterKeyType,
  GlobalFilterType,
} from 'utils/types';

import { PagesFilters } from 'containers/PageHeader/reducer';
import ShowBrandPickerButton from 'containers/PageHeader/ShowBrandPickerButton';
import { AuthSession, useAuthSession } from '@spins/react-sso-provider';

interface Props {
  basicPageFilter: PagesFilters<FilterType>;
  clientId: string;
  currentBrand: string;
  isEnterpriseContext: boolean;
  exportPdfAvailable?: boolean;
  globalFilterContext: GlobalFilterKeyType;
  pagesFilter: PagesFilters<GlobalFilterType>;
  title: string;
  weeks: number;
  onCreateGroupClick: any;
  onExportClick: any;
  onFavoritesClick: any;
  onFilterChange(newFilter: FilterSelectionType, globalFilterKey: GlobalFilterKeyType): void;
  onWeeksChange(weeks: number): void;
  onSelectedTabChange?: (selectedTab: string) => void;
  template: string;
  isBrandFilterable: boolean;
}

const PageHeaderContainer: React.FC<Props> = ({
  basicPageFilter,
  clientId,
  currentBrand,
  isEnterpriseContext,
  exportPdfAvailable,
  globalFilterContext,
  pagesFilter,
  title,
  weeks,
  onCreateGroupClick,
  onExportClick,
  onFavoritesClick,
  onFilterChange,
  onWeeksChange,
  onSelectedTabChange,
  template,
  isBrandFilterable,
}) => {
  const authSession: AuthSession = useAuthSession();
  const globalFilter = pagesFilter[globalFilterContext];
  const basicFilter = basicPageFilter[globalFilterContext];

  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const openFilterModal = () => {
    setFilterModalOpen(true);
  };

  const handleFilterChange = (newSelection: FilterSelectionType) => {
    setFilterModalOpen(false);
    onFilterChange(newSelection, globalFilterContext);
  };

  const handleExportClick = () => {
    onExportClick(authSession.user.email);
  };

  return (
    <React.Fragment>
      <PageHeader
        {...(isBrandFilterable && {
          weeks: globalFilter.weeks,
          onCreateGroupClick: onCreateGroupClick,
          onFilterClick: () => openFilterModal(),
        })}
        title={title}
        onExportClick={handleExportClick}
        onFavoritesClick={onFavoritesClick}
        onSelectedTabChange={onSelectedTabChange}
        onWeekScopeSelect={onWeeksChange}
      >
        {isEnterpriseContext && <ShowBrandPickerButton />}
      </PageHeader>
      <FavoritesModal globalFilter={globalFilter} />
      <CreateGroupModal />
      <ExportModal
        template={template}
        globalFilter={globalFilter}
        pdfAvailable={exportPdfAvailable}
      />
      {filterModalOpen && (
        <FilterModal
          {...(isEnterpriseContext && { brand: currentBrand })}
          clientId={clientId}
          currentFilter={basicFilter}
          show={filterModalOpen}
          weeks={weeks}
          onClose={() => setFilterModalOpen(false)}
          onFilterChange={handleFilterChange}
        />
      )}
    </React.Fragment>
  );
};

const mapStateToProps = (state) => ({
  basicPageFilter: basicOriginalPagesFiltersSelector(state),
  clientId: globalSelectors.clientIdSelector(state),
  currentBrand: globalSelectors.currentBrandSelector(state),
  isEnterpriseContext: globalSelectors.isEnterpriseContextSelector(state),
  pagesFilter: pagesFiltersSelector(state),
  weeks: globalFilterWeeksSelector(state),
  isBrandFilterable: globalSelectors.isBrandFilterableSelector(state),
});

const mapDispatchToProps = (dispatch) => ({
  onCreateGroupClick: () => {
    dispatch(createGroupModalActions.open());
  },
  onExportClick: (userEmail) => {
    dispatch(exportModalActions.open(userEmail));
  },
  onFavoritesClick: () => {
    dispatch(favoritesModalActions.setShow(true));
  },
  onFilterChange: (newFilter: FilterSelectionType, globalFilterKey: GlobalFilterKeyType) => {
    dispatch(pageHeaderActions.updateGlobalFilterArrays(newFilter, globalFilterKey));
  },
  onWeeksChange: (weeks: number) => {
    dispatch(pageHeaderActions.updateGlobalFilterWeeks(weeks));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(PageHeaderContainer);
